import React, { useEffect, useState } from 'react'
import { Drawer, Radio, Input, Button, Modal, message } from 'antd'
import { useSelector } from 'react-redux'
import type { RootState } from '../../../../app/store'
import { useIsAdmin } from '../../../../app/hooks'
import {
  disablePhotoLabelFeature,
  invalidatePhotoLabelEnabledCache
} from '../../api'
import { PHOTO_LABEL_FEEDBACK_TYPES } from '../../types'
import {
  trackPhotoLabelFeedbackSubmit,
  trackPhotoLabelManageClick,
  trackPhotoLabelManageResult
} from '../../utils/track'
import type { FeedbackSource } from '../../utils/track'
import './index.scss'

const { TextArea } = Input

/** 反馈内容长度上限 */
const CONTENT_MAX = 1000

interface FeedbackDrawerProps {
  /** 是否打开 */
  visible: boolean
  /** 关闭回调（取消 / 提交 / 关闭功能后都会触发） */
  onClose: () => void
  /** 入口来源：list / manage / stats，作为埋点维度 */
  source: FeedbackSource
  /** 预填联系方式（选填） */
  defaultContact?: string
}

/**
 * 照片标签「问题反馈」抽屉。
 * - 反馈：纯前端神策埋点（enter/result 漏斗 + 内容），无后端落库。
 * - 底部「关闭此功能」（仅管理员可见）：调后端关闭整个团队照片标签功能。
 * 表单状态全部局部持有，不进 Redux。
 */
function FeedbackDrawer({
  visible,
  onClose,
  source,
  defaultContact = ''
}: FeedbackDrawerProps) {
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const isAdmin = useIsAdmin()
  const [feedbackType, setFeedbackType] = useState<string>(
    PHOTO_LABEL_FEEDBACK_TYPES[0]
  )
  const [content, setContent] = useState('')
  const [contact, setContact] = useState(defaultContact)
  const [submitting, setSubmitting] = useState(false)

  // 每次打开都重置为初始值，避免上一次的输入残留
  useEffect(() => {
    if (visible) {
      setFeedbackType(PHOTO_LABEL_FEEDBACK_TYPES[0])
      setContent('')
      setContact(defaultContact)
      setSubmitting(false)
    }
  }, [visible, defaultContact])

  const handleSubmit = () => {
    const trimmedContent = content.trim()
    if (!trimmedContent) {
      message.warning('请填写问题描述')
      return
    }
    setSubmitting(true)
    try {
      // 纯神策埋点上报：内容随该事件带上，无后端落库
      trackPhotoLabelFeedbackSubmit({
        elementFrom: source,
        feedbackType,
        content: trimmedContent,
        contact: contact.trim()
      })
      message.success('反馈已提交，感谢你的反馈！')
      onClose()
    } catch (err) {
      message.error('提交失败，请稍后再试')
    } finally {
      setSubmitting(false)
    }
  }

  // 关闭整个照片标签功能（仅管理员可见）：二次确认 → 后端置白名单 enabled=0 + 停所有标签 →
  // 失效灰度缓存 → 关抽屉。不可逆，重新开启需联系运营。埋点复用 web_photo_label_manage 的 disable_feature。
  const handleDisableFeature = () => {
    trackPhotoLabelManageClick({
      clickItem: 'disable_feature',
      tabName: 'team_label'
    })
    Modal.confirm({
      title: '关闭照片标签功能？',
      content:
        '关闭后本团队将不再显示照片标签功能（打标 / 筛选 / 管理），已创建的标签会被停用。该操作不可自助恢复，需联系运营重新开启。确定关闭？',
      okText: '确定关闭',
      okType: 'danger',
      cancelText: '取消',
      // 抽屉 zIndex=1050，确认框默认 1000 会被抽屉盖住，这里抬高到 1100
      zIndex: 1100,
      onOk: async () => {
        const t0 = Date.now()
        try {
          await disablePhotoLabelFeature(String(groupId || ''))
          trackPhotoLabelManageResult({
            actionName: 'disable_feature',
            actionResult: 'success',
            costTime: Date.now() - t0,
            labelName: '',
            tabName: 'team_label'
          })
          message.success('照片标签功能已关闭，页面即将刷新')
          invalidatePhotoLabelEnabledCache(String(groupId || ''))
          onClose()
          // 整页刷新：让全局重新拉灰度状态，所有入口立即消失。
          // 否则当前页缓存的 photoLabelEnabled 还是旧值，功能看着还在。
          setTimeout(() => window.location.reload(), 600)
        } catch (err) {
          const msg = (err as Error)?.message || '关闭失败，请稍后再试'
          message.error(msg)
          trackPhotoLabelManageResult({
            actionName: 'disable_feature',
            actionResult: 'fail',
            costTime: Date.now() - t0,
            errorMsg: msg,
            labelName: '',
            tabName: 'team_label'
          })
        }
      }
    })
  }

  return (
    <Drawer
      title="照片标签 · 问题反馈"
      size={420}
      zIndex={1050}
      open={visible}
      onClose={onClose}
      destroyOnHidden
      className="photo-label-feedback-drawer"
    >
      <p className="plfd-tip">
        使用照片标签遇到问题或有建议？写下来告诉我们，会同步给产品团队跟进。
      </p>

      <div className="plfd-field">
        <div className="plfd-label">问题类型</div>
        <Radio.Group
          value={feedbackType}
          onChange={(e) => setFeedbackType(e.target.value)}
        >
          {PHOTO_LABEL_FEEDBACK_TYPES.map((type) => (
            <Radio key={type} value={type} className="plfd-radio">
              {type}
            </Radio>
          ))}
        </Radio.Group>
      </div>

      <div className="plfd-field">
        <div className="plfd-label">
          问题描述 <span className="plfd-required">*</span>
        </div>
        <TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="具体描述遇到的问题或建议，方便我们定位"
          rows={5}
          maxLength={CONTENT_MAX}
        />
        <div className="plfd-count">
          {content.length}/{CONTENT_MAX}
        </div>
      </div>

      <div className="plfd-field">
        <div className="plfd-label">联系方式</div>
        <Input
          value={contact}
          onChange={(e) => setContact(e.target.value)}
          placeholder="手机号 / 微信，方便我们回访（选填）"
        />
      </div>

      <div className="plfd-footer">
        <Button type="primary" loading={submitting} onClick={handleSubmit}>
          提交反馈
        </Button>
      </div>

      {isAdmin && (
        <div className="plfd-danger-zone">
          <span className="plfd-danger-tip">不再需要照片标签功能？</span>
          <Button
            type="link"
            className="plfd-disable"
            onClick={handleDisableFeature}
          >
            关闭此功能
          </Button>
        </div>
      )}
    </Drawer>
  )
}

export default FeedbackDrawer
export type { FeedbackDrawerProps }
